import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Container, Grid, Card, CardContent, Typography, Button, Box, Fade, Slide, useTheme, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import { LocalFlorist, Speed, Palette, ArrowBackIos, ArrowForwardIos, Star, LocalShipping, SupportAgent, Verified, CardGiftcard, Security, SentimentSatisfiedAlt, AccessTime, Favorite, FavoriteBorder } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { db } from '../firebase';
import { collection, onSnapshot, collectionGroup } from 'firebase/firestore';
import { useDesign } from '../context/DesignContext';
import { useWishlist } from '../context/WishlistContext';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: string;
}

interface Category {
  id: string;
  name: string;
  image: string;
}

const HomePage = () => {
  const { config } = useDesign();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [bestSellerIds, setBestSellerIds] = useState<Record<string, number>>({}); // id -> total vendidos
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showContent, setShowContent] = useState(false);
  const scrollContainerRef = useRef<HTMLElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [carouselWidth, setCarouselWidth] = useState<number | string>('100%');
  const { toggleWishlist, isInWishlist } = useWishlist();

  useEffect(() => {
    const calculateWidth = () => {
      // Calcular ancho disponible (basado en el ancho de la ventana o contenedor padre)
      // Restamos padding extra (32px) para asegurar que el efecto hover no se corte
      const paddingX = 32; // 16px a cada lado
      const availableWidth = Math.min(window.innerWidth - 32, 1200) - paddingX; 
      
      const itemSize = isMobile ? 100 : 160;
      const gapSize = 64; // gap: 8 = 64px
      
      // Cuántos ítems caben enteros
      // ancho = n * item + (n-1) * gap
      const n = Math.floor((availableWidth + gapSize) / (itemSize + gapSize));
      const exactWidth = n * (itemSize + gapSize) - gapSize;
      
      // Sumamos el padding al ancho final del contenedor
      const totalWidth = exactWidth + paddingX;

      setCarouselWidth(totalWidth > 0 ? totalWidth : '100%');
    };

    calculateWidth();
    window.addEventListener('resize', calculateWidth);
    return () => window.removeEventListener('resize', calculateWidth);
  }, [isMobile]);

  const scrollCategories = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { current } = scrollContainerRef;
      const scrollAmount = direction === 'left' ? -300 : 300;
      current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const defaultHomeImages = useMemo(() => ['https://images.unsplash.com/photo-1495231916356-6c0d1b20c7cb?w=1600'], []);

  const homeImages = config.homeImages && config.homeImages.length > 0
    ? config.homeImages
    : defaultHomeImages;

  useEffect(() => {
    setShowContent(true);
  }, []);

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev === 0 ? homeImages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % homeImages.length);
  };

  useEffect(() => {
    if (homeImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % homeImages.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [homeImages, currentSlide]);

  // Suscribir categorías desde Firestore
  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(collection(db, 'categories'), (snap) => {
      const cats: Category[] = [];
      snap.docs.forEach((d) => {
        const data = d.data() as any;
        if (data.active === false) return; // Filtrar inactivas
        
        cats.push({
          id: d.id,
          name: data.name || 'Sin nombre',
          image: data.image || 'https://via.placeholder.com/150',
          order: data.order
        } as any);
      });
      cats.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
      setCategories(cats);
    }, (err) => {
      console.warn('[Home] Error suscribiendo categorías', err);
      setCategories([]);
    });
    return () => unsub();
  }, []);



  // Suscribir productos desde Firestore
  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(collection(db, 'products'), (snap) => {
      const prods: Product[] = [];
      snap.docs.forEach((d) => {
        const data = d.data() as any;
        // Filtrar si visible es false
        if (data.visible === false) return;

        const image = data.imageUrl || (Array.isArray(data.images) && data.images.length ? data.images[0] : 'https://via.placeholder.com/400x250?text=Producto');
        prods.push({
          id: d.id,
          name: data.name || d.id,
          description: data.description || '',
          price: typeof data.price === 'number' ? data.price : 0,
          image,
          category: (data.categoryId || '').toString(),
        });
      });
      setProducts(prods);
    }, (err) => {
      console.warn('[Home] Error suscribiendo productos', err);
      setProducts([]);
    });
    return () => unsub();
  }, []);

  // Intentar derivar más vendidos a partir de orders (collectionGroup) si las reglas lo permiten
  useEffect(() => {
    if (!db) return;
    let unsub: (() => void) | null = null;
    try {
      unsub = onSnapshot(collectionGroup(db, 'orders'), (snap) => {
        const counts: Record<string, number> = {};
        snap.docs.forEach((doc) => {
          const data = doc.data() as any;
          const items = Array.isArray(data.items) ? data.items : [];
          items.forEach((it: any) => {
            const pid = (it.productId || '').toString();
            const qty = Number(it.quantity || 0);
            if (!pid || !qty) return;
            counts[pid] = (counts[pid] || 0) + qty;
          });
        });
        setBestSellerIds(counts);
      }, (err) => {
        // Ignorar error de permisos (esperado para usuarios no admin)
        if (err.code !== 'permission-denied') {
          console.warn('[Home] Error suscribiendo orders', err);
        }
        setBestSellerIds({});
      });
    } catch (e) {
      // Si falla (reglas/índices), dejamos bestSellerIds vacío y usaremos fallback
      console.warn('[Home] No se pudo leer pedidos para más vendidos', e);
      setBestSellerIds({});
    }
    return () => {
      if (unsub) {
        try { unsub(); } catch {}
      }
    };
  }, []);

  // Derivar lista de destacados: más vendidos si hay conteos; fallback a primeros productos
  const featuredProducts = useMemo(() => {
    if (products.length === 0) return [] as Product[];
    const entries = Object.entries(bestSellerIds);
    if (entries.length) {
      const sortedIds = entries.sort((a, b) => b[1] - a[1]).map(([id]) => id);
      const byId = new Map(products.map((p) => [p.id, p]));
      const top = sortedIds
        .map((id) => byId.get(id))
        .filter(Boolean) as Product[];
      // Si hay menos de 6, completar con productos restantes
      const remaining = products.filter((p) => !sortedIds.includes(p.id));
      const combined = [...top, ...remaining].slice(0, 6);
      return combined;
    }
    // Fallback: sin pedidos, mostrar hasta 6 productos del catálogo
    return products.slice(0, 6);
  }, [products, bestSellerIds]);

  const services = (config.servicesContent && config.servicesContent.length > 0 ? config.servicesContent : [
    { icon: 'speed', title: 'Envío Express', description: 'Entrega en 2 horas en zonas seleccionadas' },
    { icon: 'palette', title: 'Diseño Exclusivo', description: 'Arreglos únicos creados por expertos floristas' },
    { icon: 'flower', title: 'Frescura Garantizada', description: 'Seleccionamos las flores más frescas cada día' }
  ]).map(s => ({
    ...s,
    icon: s.icon === 'speed' ? <Speed sx={{ fontSize: 48 }} /> :
          s.icon === 'palette' ? <Palette sx={{ fontSize: 48 }} /> :
          s.icon === 'flower' ? <LocalFlorist sx={{ fontSize: 48 }} /> :
          s.icon === 'shipping' ? <LocalShipping sx={{ fontSize: 48 }} /> :
          s.icon === 'support' ? <SupportAgent sx={{ fontSize: 48 }} /> :
          s.icon === 'verified' ? <Verified sx={{ fontSize: 48 }} /> :
          s.icon === 'gift' ? <CardGiftcard sx={{ fontSize: 48 }} /> :
          s.icon === 'security' ? <Security sx={{ fontSize: 48 }} /> :
          s.icon === 'happy' ? <SentimentSatisfiedAlt sx={{ fontSize: 48 }} /> :
          s.icon === 'time' ? <AccessTime sx={{ fontSize: 48 }} /> :
          <Star sx={{ fontSize: 48 }} />
  }));

  return (
    <Box sx={{ backgroundColor: '#fff', minHeight: '100vh', paddingBottom: 8 }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: '40vh',
          minHeight: '350px',
          width: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          backgroundColor: '#000',
        }}
      >
        {/* Background Images Carousel */}
        {homeImages.map((img, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${img})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: index === currentSlide ? 1 : 0,
              transition: 'opacity 1.5s ease-in-out',
              zIndex: 0,
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.5) 100%)',
              }
            }}
          />
        ))}

        {/* Navigation Arrows */}
        {homeImages.length > 1 && !isMobile && (
          <>
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: 32,
                zIndex: 2,
                color: 'rgba(255,255,255,0.8)',
                bgcolor: 'rgba(0,0,0,0.2)',
                backdropFilter: 'blur(4px)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.4)', color: '#fff' }
              }}
            >
              <ArrowBackIos fontSize="large" />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 32,
                zIndex: 2,
                color: 'rgba(255,255,255,0.8)',
                bgcolor: 'rgba(0,0,0,0.2)',
                backdropFilter: 'blur(4px)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.4)', color: '#fff' }
              }}
            >
              <ArrowForwardIos fontSize="large" />
            </IconButton>
          </>
        )}

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Fade in={showContent} timeout={1000}>
            <Box>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  color: '#fff',
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '4.5rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  letterSpacing: '0.05em',
                }}
              >
                {config.heroTitle || 'Arte Floral Exquisito'}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  mb: 5,
                  fontSize: { xs: '1.1rem', md: '1.5rem' },
                  fontWeight: 300,
                  maxWidth: '800px',
                  mx: 'auto',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }}
              >
                {config.heroSubtitle || 'Creamos momentos inolvidables con la belleza natural de las flores'}
              </Typography>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/catalog"
                sx={{
                  backgroundColor: 'primary.main',
                  color: '#fff',
                  px: 6,
                  py: 1.8,
                  fontSize: '1rem',
                  borderRadius: '50px',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                  textTransform: 'none',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 25px rgba(0,0,0,0.3)',
                  }
                }}
              >
                Explorar Colección
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Categories Section */}
      <Box sx={{ pt: 6, pb: 0, backgroundColor: '#fff' }}>
        <Container maxWidth="lg">
          <Typography
             variant="h4"
             component="h2"
             sx={{ 
               mb: 4, 
               fontFamily: '"Playfair Display", serif', 
               fontWeight: 700,
               textAlign: { xs: 'left', md: 'center' },
               color: '#333'
             }}
          >
            Categorías
          </Typography>
          
          <Box ref={wrapperRef} sx={{ position: 'relative', display: 'flex', alignItems: 'center', width: carouselWidth, mx: 'auto' }}>
            {!isMobile && categories.length > 4 && (
              <IconButton
                onClick={() => scrollCategories('left')}
                sx={{
                  position: 'absolute',
                  left: -20,
                  zIndex: 2,
                  bgcolor: '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  '&:hover': { bgcolor: '#f5f5f5' }
                }}
              >
                <ArrowBackIos />
              </IconButton>
            )}

            <Box
              ref={scrollContainerRef}
              sx={{
                display: 'flex',
                overflowX: 'auto',
                pt: 4,
                pb: 2,
                gap: 8,
                width: '100%',
                justifyContent: 'flex-start',
                px: 2,
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                scrollBehavior: 'smooth'
              }}
            >
              {categories.map((cat) => (
                <Box
                  key={cat.id}
                  component={Link}
                  to="/catalog"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textDecoration: 'none',
                    width: { xs: 100, md: 160 },
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.05)' }
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 100, md: 160 },
                      height: { xs: 100, md: 160 },
                      borderRadius: '50%',
                      overflow: 'hidden',
                      mb: 1.5,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      border: '2px solid transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        borderColor: 'primary.main',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={cat.image}
                      alt={cat.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{ 
                      color: '#333', 
                      fontWeight: 600,
                      textAlign: 'center',
                      fontSize: { xs: '0.9rem', md: '1.1rem' }
                    }}
                  >
                    {cat.name}
                  </Typography>
                </Box>
              ))}
            </Box>

            {!isMobile && categories.length > 4 && (
              <IconButton
                onClick={() => scrollCategories('right')}
                sx={{
                  position: 'absolute',
                  right: -20,
                  zIndex: 2,
                  bgcolor: '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  '&:hover': { bgcolor: '#f5f5f5' }
                }}
              >
                <ArrowForwardIos />
              </IconButton>
            )}
          </Box>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ width: '100%', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ backgroundColor: config.servicesSectionColor || '#a733ff', borderRadius: 8, p: 4 }}>
            <Grid container spacing={6} justifyContent="center">
              {services.map((service, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                      }
                    }}
                  >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      color: '#fff',
                      mb: 3
                    }}
                  >
                    {service.icon}
                  </Box>
                  <Typography variant="h5" component="h3" sx={{ mb: 2, fontFamily: '"Playfair Display", serif', fontWeight: 600, color: '#fff' }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7 }}>
                      {service.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Featured Products Section */}
      <Box sx={{ backgroundColor: '#fafafa', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.2em', mb: 1, display: 'block' }}
            >
              NUESTRA SELECCIÓN
            </Typography>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                color: '#1a1a1a',
                fontFamily: '"Playfair Display", serif',
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              Más Vendidos
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {featuredProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#fff',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      paddingTop: '100%', // 1:1 Aspect Ratio
                      backgroundColor: '#f0f0f0',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      component={Link}
                      to={`/product/${product.id}`}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'block'
                      }}
                    >
                      <Box
                        component="img"
                        src={product.image}
                        alt={product.name}
                        loading="lazy"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease',
                          '&:hover': {
                            transform: 'scale(1.1)'
                          }
                        }}
                      />
                    </Box>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleWishlist(product.id);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                        color: isInWishlist(product.id) ? '#E91E63' : 'action.active',
                      }}
                    >
                      {isInWishlist(product.id) ? <Favorite /> : <FavoriteBorder />}
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      component={Link}
                      to={`/product/${product.id}`}
                      variant="h6"
                      sx={{
                        display: 'block',
                        fontFamily: '"Roboto", sans-serif',
                        fontWeight: 600,
                        mb: 1,
                        color: '#1a1a1a',
                        textDecoration: 'none',
                        lineHeight: 1.2,
                        '&:hover': { color: 'primary.main' }
                      }}
                    >
                      {product.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', flexGrow: 1 }}>
                      {product.description}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography
                        variant="h6"
                        sx={{ color: 'primary.main', fontWeight: 700 }}
                      >
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(product.price)}
                      </Typography>
                      <Button
                        component={Link}
                        to={`/product/${product.id}`}
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{ 
                          fontWeight: 600,
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2
                        }}
                      >
                        Ver
                      </Button>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Box textAlign="center" mt={8}>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/catalog"
              sx={{
                borderColor: '#333',
                color: '#333',
                px: 5,
                py: 1.5,
                borderRadius: '50px',
                borderWidth: '2px',
                fontWeight: 600,
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  borderWidth: '2px',
                  backgroundColor: 'transparent'
                }
              }}
            >
              Ver Todo el Catálogo
            </Button>
          </Box>
        </Container>
      </Box>

      {/* About Section - New */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={config.aboutUs?.imageUrl || "https://images.unsplash.com/photo-1526047932273-341f2a7631f9?w=800"}
              alt="Florista trabajando"
              sx={{
                width: '100%',
                borderRadius: '20px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.2em', mb: 1, display: 'block' }}>
              SOBRE NOSOTROS
            </Typography>
            <Typography variant="h3" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
              {config.aboutUs?.title || 'Pasión por los detalles'}
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.8 }}>
              {config.aboutUs?.description || 'En Blüm, entendemos que cada flor cuenta una historia. Desde hace más de 10 años, nos dedicamos a seleccionar las flores más frescas y hermosas para crear arreglos que expresan lo que las palabras a veces no pueden. Nuestro compromiso es entregar no solo flores, sino emociones, directamente a la puerta de tus seres queridos.'}
            </Typography>
            <Button
              component={Link}
              to="/catalog"
              sx={{
                mt: 2,
                color: 'primary.main',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                '&:hover': {
                  background: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              Conoce más sobre nosotros &rarr;
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HomePage;